import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AppComponent } from './app.component';
import { BatchAnalysisComponent } from './components/batch-stats/batch-analysis.component';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NgOptimizedImage, registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { IconsProviderModule } from './icons-provider.module';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzDrawerModule } from "ng-zorro-antd/drawer";
import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzGridModule } from "ng-zorro-antd/grid";
import { CatalogsComponent } from "./pages/catalogs/catalogs.component";
import { NzPageHeaderModule } from "ng-zorro-antd/page-header";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzDropdownModule } from "ng-zorro-antd/dropdown";
import { NzTableModule } from "ng-zorro-antd/table";
import { NzSwitchModule } from "ng-zorro-antd/switch";
import { PoComponent } from "./pages/po/po.component";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzDescriptionsModule } from "ng-zorro-antd/descriptions";
import { NzListModule } from "ng-zorro-antd/list";
import { NzCardModule } from "ng-zorro-antd/card";
import { ScanComponent } from "./pages/scan/scan.component";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzImageModule } from "ng-zorro-antd/image";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NGXS_PLUGINS, NgxsModule } from "@ngxs/store";
import { GpioState } from "../store/gpio/gpio.state";
import { GpioComponent } from "./pages/gpio/gpio.component";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { BackendService } from "../services/backend";
import { provideNgxsDataPlugin } from "@angular-ru/ngxs";
import { DateJsonParser, JsonHttpInterceptor, JsonParser } from "../services/json-interceptor";
import { ProductionBatchState } from "../store/batch/batch.state";
import { ScannerState } from 'src/store/scanner/scanner.state';
import { ScannerControllerComponent } from "./components/scanner-controller/scanner-controller.component";
import { NzSliderModule } from "ng-zorro-antd/slider";
import { NzInputNumberModule } from "ng-zorro-antd/input-number";
import { CatalogState } from "../store/catalog/catalog.state";
import { NzUploadModule } from "ng-zorro-antd/upload";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { PipesModule } from "./pipes/pipes.module";
import { ComponentsModule } from "./components/components.module";
import { LuxonModule } from "luxon-angular";
import { NzAutocompleteModule } from "ng-zorro-antd/auto-complete";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";
import { HomeModule } from "./pages/home/home.module";
import { ErrorState } from "../store/error/error.state";
import { NzAlertModule } from "ng-zorro-antd/alert";
import { ErrorCatchingInterceptor } from "../services/error-interceptor";
import { AuthenticationState } from "../store/auth/auth.state";
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { ApiKeyInterceptor } from "../services/apikey-interceptor";
import { ModelState } from "../store/model/model.state";
import { LogRocketPlugin } from "../services/logrocket-plugin";
import { ActionInterceptor } from "../services/action-interceptor";
import { TimingInterceptor } from "../services/timing-interceptor";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzTabsModule } from "ng-zorro-antd/tabs";
import { NzInputModule } from "ng-zorro-antd/input";
import * as PlotlyJS from 'plotly.js-dist-min';
import { PlotlyModule } from 'angular-plotly.js';
import { SseStatusComponent } from "./components/sse-status/sse-status.component";
import { OperatorReasonDialogComponent } from "./components/operator-reason-dialog/operator-reason-dialog.component";
import { ClientConfigService } from "../services/client-config.service";
import { provideMarkdown } from 'ngx-markdown';


// add these to support the other languages at runtime:
import he from '@angular/common/locales/he';
import ru from '@angular/common/locales/ru';
registerLocaleData(en);
registerLocaleData(he);
registerLocaleData(ru);

/**
 * APP_INITIALIZER factory (T-016 AC-3). Injecting `AuthenticationState` forces Angular DI to
 * construct it — which forces `NgxsStoragePluginModule` to rehydrate `auth.token` from
 * `localStorage` — BEFORE `isAuthenticated` is read synchronously below. If there is no valid
 * session, skip the network call entirely and resolve immediately (the existing `authGuard`
 * still redirects to `/welcome`). Exported as a named function (not an inline arrow) so it is
 * unit-testable in isolation.
 */
export function clientConfigInitializerFactory(
  authState: AuthenticationState,
  clientConfig: ClientConfigService
): () => Promise<void> {
  return () => {
    if (!authState.isAuthenticated) {
      return Promise.resolve();
    }
    return firstValueFrom(clientConfig.fetch()).then(() => undefined);
  };
}

@NgModule({
  declarations: [
    AppComponent,
    CatalogsComponent,
    PoComponent,
    ScanComponent,
    GpioComponent,
    ScannerControllerComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    SseStatusComponent,
    OperatorReasonDialogComponent,
    FormsModule,
    AppRoutingModule,
    IconsProviderModule,
    NgxsModule.forRoot([
      GpioState,
      ProductionBatchState,
      AuthenticationState,
      ScannerState,
      CatalogState,
      ErrorState,
      ModelState
    ]),
    NgxsStoragePluginModule.forRoot({
      keys: ['auth.token']
    }),
    //NgxsLoggerPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NzLayoutModule,
    NzMenuModule,
    NzDrawerModule,
    NzSpaceModule,
    PipesModule,
    NzGridModule,
    NzPageHeaderModule,
    NzButtonModule,
    NzDropdownModule,
    NzTableModule,
    NzSwitchModule,
    NzTagModule,
    NzDescriptionsModule,
    NzListModule,
    NzCardModule,
    NzModalModule,
    NzImageModule,
    NzRadioModule,
    NzCheckboxModule,
    NzSliderModule,
    NzInputNumberModule,
    NzUploadModule,
    NzSelectModule,
    ComponentsModule,
    LuxonModule,
    NzAutocompleteModule,
    NzTooltipModule,
    HomeModule,
    NzAlertModule,
    NgOptimizedImage,
    NzSpinModule,
    NzTabsModule,
    NzInputModule,
    PlotlyModule.forRoot(PlotlyJS),
    BatchAnalysisComponent,
  ],
  providers: [
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideNgxsDataPlugin(),
    // Markdown rendering for tag descriptions. Default HTML sanitization stays ON (do not pass `sanitize`).
    provideMarkdown(),
    { provide: NZ_I18N, useValue: en_US },
    { provide: HTTP_INTERCEPTORS, useClass: ApiKeyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorCatchingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ActionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JsonHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimingInterceptor, multi: true },
    { provide: JsonParser, useClass: DateJsonParser },
    { provide: Window, useValue: window },
    BackendService,
    {
      provide: APP_INITIALIZER,
      useFactory: clientConfigInitializerFactory,
      deps: [AuthenticationState, ClientConfigService],
      multi: true,
    },
    {
      provide: NGXS_PLUGINS,
      useClass: LogRocketPlugin,
      multi: true
    },
  ],
  exports: [],
  bootstrap: [AppComponent]
})
export class AppModule {
}
